import { alpha, Box, Grid, Tooltip, Typography, useTheme } from '@mui/material';

import { resolveFeeDescription } from '@/utils/fees/feeDescriptions';
import { formatPrice } from '@/utils/helper';
import { ProductEsitmation } from '@/types/ProductType';

type ProductDetailCashToCloseProps = {
  estimation: ProductEsitmation;
};

export function productDetailCashToCloseHasContent(
  estimation: ProductEsitmation | null | undefined
): boolean {
  if (!estimation) return false;
  return (
    estimation.cashToClose != null && Number.isFinite(estimation.cashToClose)
  );
}

function CashToCloseRow({
  label,
  value,
  credit = false,
}: {
  label: string;
  value: number;
  credit?: boolean;
}) {
  const theme = useTheme();
  const tip = resolveFeeDescription({ label });

  return (
    <Grid item xs={12} sm={6}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.greyShades.border1}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Tooltip title={tip} placement="top" arrow enterTouchDelay={0}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              cursor: 'help',
              borderBottom: `1px dotted ${alpha(
                theme.palette.text.secondary,
                0.45
              )}`,
              display: 'inline-block',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight={700}>
          {credit
            ? `− ${formatPrice(Math.abs(value), 0)}`
            : formatPrice(value, 0)}
        </Typography>
      </Box>
    </Grid>
  );
}

export const ProductDetailCashToClose = ({
  estimation,
}: ProductDetailCashToCloseProps) => {
  const theme = useTheme();

  if (!productDetailCashToCloseHasContent(estimation)) return null;

  const refi = (
    estimation as {
      refinanceCashFlow?: {
        payoff: number;
        newLoan: number;
        closingCosts: number;
        cashToClose: number;
        payoffKnown?: boolean;
      };
    }
  ).refinanceCashFlow;
  if (refi && refi.payoffKnown === false) {
    return (
      <Box>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ mb: 1, color: theme.palette.text.primary }}
        >
          Cash at closing
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add your current mortgage balance to see your payoff and how much
          you'd bring to — or get back at — closing.
        </Typography>
      </Box>
    );
  }
  if (refi) {
    const cashBack = refi.cashToClose < 0;
    const refiRows = [
      { label: 'New loan amount', value: refi.newLoan, credit: true },
      { label: 'Payoff of current loan', value: refi.payoff },
      { label: 'Closing costs', value: refi.closingCosts },
      {
        label: cashBack ? 'Cash back to you' : 'Cash to close',
        value: Math.abs(refi.cashToClose),
      },
    ];
    return (
      <Box>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ mb: 2, color: theme.palette.text.primary }}
        >
          {cashBack ? 'Cash back at closing' : 'Cash to close'}
        </Typography>
        <Grid container spacing={1.5}>
          {refiRows.map((row) =>
            !Number.isFinite(row.value) ? null : (
              <CashToCloseRow
                key={row.label}
                label={row.label}
                value={row.value}
                credit={row.credit}
              />
            )
          )}
        </Grid>
      </Box>
    );
  }

  // Cash to close already nets out the earnest money deposit; list it so the
  // card adds up (it read as down payment + closing costs − an unexplained
  // $1,000).
  const earnestMoney = Number(
    (estimation as { earnestMoneyDeposit?: number }).earnestMoneyDeposit
  );
  const rows: { label: string; value: number | undefined; credit?: boolean }[] =
    [
      { label: 'Down payment', value: (estimation as any).downPayment },
      {
        label: 'Closing costs',
        value: (estimation as { closingCosts?: number }).closingCosts,
      },
      ...(earnestMoney > 0
        ? [
            {
              label: 'Earnest money deposit (paid at contract)',
              value: earnestMoney,
              credit: true,
            },
          ]
        : []),
      { label: 'Cash to close', value: estimation.cashToClose },
    ];

  const unbeatableDeal = (estimation as { unbeatableDeal?: number })
    .unbeatableDeal;
  if (unbeatableDeal != null && Number.isFinite(unbeatableDeal)) {
    rows.push({
      label: 'Unbeatable deal (after credit)',
      value: unbeatableDeal,
    });
  }

  return (
    <Box>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ mb: 2, color: theme.palette.text.primary }}
      >
        Cash to close
      </Typography>
      <Grid container spacing={1.5}>
        {rows.map((row) =>
          row.value == null || !Number.isFinite(row.value) ? null : (
            <CashToCloseRow
              key={row.label}
              label={row.label}
              value={row.value}
              credit={row.credit}
            />
          )
        )}
      </Grid>
    </Box>
  );
};
